import React, { Component } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import cx from 'classnames';
import filter from 'ramda/src/filter';
import isEmpty from 'ramda/src/isEmpty';
import isNil from 'ramda/src/isNil';
import Avatar from '@components/@core/Avatar';
import DatePicker from '@components/@core/DatePicker';
import EditDropdown from '@components/EditDropdown';
import Icon from '@components/@core/Icon';
import Pagination from '@components/@core/Pagination';
import { convertAvgDays, inPage } from '~/services/util';
import { exportCsv } from '~/services/util-excel';
import { ReportMenu } from '~/services/config';

const PdfIcon = loadable(() => import('./PdfIcon'), { ssr: false });

const moment = extendMoment(originalMoment);

class TaskReport extends Component {
    state = {
        dateStart: moment().startOf('month').format('YYYY-MM-DD'),
        dateEnd: moment().endOf('month').format('YYYY-MM-DD'),
        current: 1,
        per: 25,
        showPrint: false,
    };

    handleDateChange = (e) => {
        const dateStart = moment(e.start).format('YYYY-MM-DD');
        const dateEnd = moment(e.end).format('YYYY-MM-DD');
        this.props.onTasks(dateStart, dateEnd);
        this.setState({ dateStart, dateEnd });
    };

    handleExcelReport = () => {
        this.props.onModal({
            type: 'Confirm',
            data: {
                before: {
                    title: 'Confirm',
                    body: 'Would you like to download this reports data to excel?',
                },
            },
            callBack: {
                onYes: () => {
                    this.handleExcel();
                },
            },
        });
    };

    handleExcel = () => {
        const { data } = this.props;
        let companyTask = [];
        data.individuals.map((item) => {
            const excelData = {
                Requirements: item.name,
                'Task \nAssigned for month': `${item.report.tasks_assigned.count} \n100%`,
                'Task \nIncompl. for month': `${item.report.tasks_incomplete.count} \n${item.report.tasks_incomplete.percent}%`,
                'Task \nCompl. for month': `${item.report.tasks_completed.count} \n${item.report.tasks_completed.percent}%`,
                'Task \nCompl. on time': `${item.report.tasks_completed_on_time.count} \n${item.report.tasks_completed_on_time.percent}%`,
                'Task \nCompl. late': `${item.report.tasks_completed_late.count} \n${item.report.tasks_completed_late.percent}%`,
                'Task \nAve Compl. per day': item.report.avg_completions_per_day.toFixed(2),
                'Task \nAve time to compl.': convertAvgDays(item.report.avg_days.count),
                'Daily Habit': `${item.report.daily_completion.complete} \n${item.report.daily_completion.percent}%`,
                'Weekly Habit': `${item.report.weekly_completion.complete} \n${item.report.weekly_completion.percent}%`,
                'Monthly Habit': `${item.report.monthly_completion.complete} \n${item.report.monthly_completion.percent}%`,
            };
            companyTask.push(excelData);
        });
        const key = [
            'Requirements',
            'Task \nAssigned for month',
            'Task \nIncompl. for month',
            'Task \nCompl. for month',
            'Task \nCompl. on time',
            'Task \nCompl. late',
            'Task \nAve Compl. per day',
            'Task \nAve time to compl.',
            'Daily Habit',
            'Weekly Habit',
            'Monthly Habit',
        ];
        exportCsv(companyTask, key, 'Company Task Report', true);
    };

    handleReportMenu = (e) => {
        switch (e) {
            case 'export':
                this.handleExcelReport();
                break;
            case 'print':
                this.setState({ showPrint: true });
                break;
        }
    };

    render() {
        const { data, selected } = this.props;
        const { current, per, dateStart, dateEnd } = this.state;
        const individuals = selected ? filter((e) => selected.includes(e.id), data.individuals) : data.individuals;

        return (
            <div className="task-report card p-0">
                <div className="d-h-between mb-2 mb-md-4 my-4">
                    <span className="dsl-b22 bold text-capitalize ps-4 ssp-l-0">{isNil(selected) ? 'Company' : 'Individual'} Task Report</span>
                    <div className="d-flex ai-center pe-4">
                        <DatePicker calendar="range" append="caret" format="MMM D" as="span" align="right" value={moment.range(dateStart, dateEnd)} onSelect={this.handleDateChange} />
                        <EditDropdown className="ml-3" dataCy="editDropdown" options={ReportMenu} onChange={this.handleReportMenu} />
                        {this.state.showPrint && <PdfIcon state={this.state} props={this.props} name="Print" />}
                    </div>
                </div>
                <div className="d-flex">
                    <div className="w-100">
                        <div className="d-flex">
                            <p className="d-flex-6 dsl-b16 tasks-start me-stats bold text-start ms-4 desktop-screen">Tasks Stats</p>
                            <p className="d-flex-3 dsl-b16 tasks-start bold text-start ms-0 ps-3 desktop-screen">Habit Completion</p>
                        </div>
                        <div className="d-flex list-item mx-4 desktop-screen">
                            <div className="d-flex-2 d-flex-sm-2">
                                <p className="dsl-m12 text-400">Requirements</p>
                            </div>
                            <div className="d-flex d-flex-7">
                                <div className="d-flex-1 text-end">
                                    <p className="dsl-m12 mb-1 text-400">Assigned</p>
                                    <p className="dsl-m12 text-400">for month</p>
                                </div>
                                <div className="d-flex-1 text-end">
                                    <p className="dsl-m12 mb-1 text-400">Incomplete</p>
                                    <p className="dsl-m12 text-400">for month</p>
                                </div>
                                <div className="d-flex-1 text-end">
                                    <p className="dsl-m12 mb-1 text-400">Compl.</p>
                                    <p className="dsl-m12 text-400">for month</p>
                                </div>
                                <div className="d-flex-1 text-end">
                                    <p className="dsl-m12 mb-1 text-400">Compl.</p>
                                    <p className="dsl-m12 text-400">on time</p>
                                </div>
                                <div className="d-flex-1 text-end">
                                    <p className="dsl-m12 mb-1 text-400">Compl.</p>
                                    <p className="dsl-m12 text-400">late</p>
                                </div>
                                <div className="d-flex-1 text-end">
                                    <p className="dsl-m12 mb-1 text-400">Ave Compl.</p>
                                    <p className="dsl-m12 text-400">per day</p>
                                </div>
                                <div className="d-flex-1 text-end">
                                    <p className="dsl-m12 mb-1 text-400">Ave Time</p>
                                    <p className="dsl-m12 text-400">to compl.</p>
                                </div>
                            </div>
                            <div className="pl-5 d-flex-3 d-flex">
                                <p className="d-flex-1 dsl-m12 text-400 text-end">Daily</p>
                                <p className="d-flex-1 dsl-m12 text-400 text-end">Weekly</p>
                                <p className="d-flex-1 dsl-m12 text-400 text-end">Monthly</p>
                            </div>
                        </div>
                        {!selected && !isEmpty(data.totals) && (
                            <div className={cx('d-flex list-item hover-items px-4 ps-0 ssp-l-0', !isNil(selected) && 'border-none')}>
                                <div className="d-flex list-item hover-list-item py-0 py-md-4 ssm-column">
                                    <div className="d-flex-2 d-flex-sm-2 name-sm">
                                        <p className="dsl-b14 lh-17 mb-0 text-400">Total</p>
                                    </div>
                                    <div className="d-flex ssm-column d-flex-7 ps-0">
                                        <div className="d-flex-1 text-end d-flex d-sm-block py-2 py-sm-0">
                                            <span className="dsl-b14 mobile-screen d-flex-1 text-start">Assign For Month</span>
                                            <p className="dsl-b14 lh-17 mb-1 text-400 me-0 ssm-r-2">{data.totals.tasks_assigned.count}</p>
                                            <p className="dsl-m12 mb-0 text-400">100%</p>
                                        </div>
                                        <div className="d-flex-1 text-end d-flex d-sm-block py-2 py-sm-0">
                                            <span className="dsl-b14 mobile-screen d-flex-1 text-start">Incomplete For Month</span>
                                            <p className="dsl-b14 lh-17 mb-1 text-400 me-0 ssm-r-2">{data.totals.tasks_incomplete.count}</p>
                                            <p className="dsl-m12 mb-0 text-400">{data.totals.tasks_incomplete.percent}%</p>
                                        </div>
                                        <div className="d-flex-1 text-end pt-3 pt-md-0 ssp-t-2 d-flex d-sm-block py-2 py-sm-0">
                                            <span className="dsl-b14 mobile-screen d-flex-1 text-start">Compl. For Month</span>
                                            <p className="dsl-b14 lh-17 mb-1 text-400 me-0 ssm-r-2">{data.totals.tasks_completed.count}</p>
                                            <p className="dsl-m12 mb-0 text-400">{data.totals.tasks_completed.percent}%</p>
                                        </div>
                                        <div className="d-flex-1 text-end d-flex d-sm-block py-2 py-sm-0">
                                            <span className="dsl-b14 mobile-screen d-flex-1 text-start">Compl. on time</span>
                                            <p className="dsl-b14 lh-17 mb-1 text-400 me-0 ssm-r-2">{data.totals.tasks_completed_on_time.count}</p>
                                            <p className="dsl-m12 mb-0 text-400">{data.totals.tasks_completed_on_time.percent}%</p>
                                        </div>
                                        <div className="d-flex-1 text-end d-flex d-sm-block py-2 py-sm-0">
                                            <span className="dsl-b14 mobile-screen d-flex-1 text-start">Compl. late</span>
                                            <p className="dsl-b14 lh-17 mb-1 text-400 me-0 ssm-r-2">{data.totals.tasks_completed_late.count}</p>
                                            <p className="dsl-m12 mb-0 text-400">{data.totals.tasks_completed_late.percent}%</p>
                                        </div>
                                        <div className="d-flex-1 text-end d-flex d-sm-block py-2 py-sm-0">
                                            <span className="dsl-b14 mobile-screen d-flex-1 text-start">Ave Compl. Per Day</span>
                                            <p className="dsl-b14 lh-17 mb-1 text-400">{data.totals.avg_completions_per_day?.toFixed(2) || 'NA'}</p>
                                        </div>
                                        <div className="d-flex-1 text-end pt-3 ssp-t-2 d-flex d-sm-block py-2 py-sm-0">
                                            <span className="dsl-b14 mobile-screen d-flex-1 text-start me-0 ssm-r-2">Assign For Month</span>
                                            <p className="dsl-b14 lh-17 mb-1 text-400">{convertAvgDays(data.totals.avg_days?.count || 0)}</p>
                                        </div>
                                    </div>
                                    <div className="pl-5 d-flex-3 d-flex ssm-column ssp-l-0">
                                        <div className="d-flex-1 text-end d-flex d-sm-block py-2 py-sm-0">
                                            <span className="dsl-b14 mobile-screen d-flex-1 text-start">Daily</span>
                                            <p className="dsl-b14 lh-17 mb-1 text-400 me-0 ssm-r-2">{data.totals.daily_completion.complete}</p>
                                            <p className="dsl-m12 mb-1 text-400">{data.totals.daily_completion.percent}%</p>
                                        </div>
                                        <div className="d-flex-1 text-end d-flex d-sm-block py-2 py-sm-0">
                                            <span className="dsl-b14 mobile-screen d-flex-1 text-start">Weekly</span>
                                            <p className="dsl-b14 lh-17 mb-1 text-400 me-0 ssm-r-2">{data.totals.weekly_completion.complete}</p>
                                            <p className="dsl-m12 mb-1 text-400">{data.totals.weekly_completion.percent}%</p>
                                        </div>
                                        <div className="d-flex-1 text-end pt-3 pt-md-0 ssp-t-2 d-flex d-sm-block py-2 py-sm-0">
                                            <span className="dsl-b14 mobile-screen d-flex-1 text-start">Monthly</span>
                                            <p className="dsl-b14 lh-17 mb-1 text-400 me-0 ssm-r-2">{data.totals.monthly_completion.complete}</p>
                                            <p className="dsl-m12 mb-1 text-400">{data.totals.monthly_completion.percent}%</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="w-100">
                            {individuals.length > 0 ? (
                                individuals.map((item, index) => (
                                    <>
                                        {inPage(index, current, per) && (
                                            <div className={cx('d-flex list-item hover-items px-4 pt-0 ssp-t-2 ssp-l-0', !isNil(selected) && 'border-none')} key={item.id}>
                                                <div className="d-flex ssm-column hover-list-item pb-0 pt-0 pb-md-4 pt-md-4">
                                                    <div className="d-flex-2 d-flex ai-center me-2 ssm-r-0 me-md-0">
                                                        <Avatar name={item.name} url={item.avatar} />
                                                        <span className="dsl-b14 d-flex-1 lh-17 ms-3 cursor-pointer text-400 word-break-w name-sm" onClick={() => this.props.onClick(item.id)}>
                                                            {item.name}
                                                        </span>
                                                        <div className="edit-icon mobile-screen desktop-screen text-end">
                                                            <Icon name="fas fa-ellipsis-h text-500" color="#969faa" size={18} />
                                                        </div>
                                                    </div>
                                                    <div className="d-flex ssm-column d-flex-7 ps-0 ssp-l-0">
                                                        <div className="d-flex-1 text-end ssm-left d-flex d-sm-block py-2 py-sm-0 ai-center">
                                                            <span className="dsl-b14 mobile-screen d-flex-1">Assign For Month</span>
                                                            <p className="dsl-b14 lh-17 mb-1 ssm-b-0 text-400 me-0 ssm-r-2">{item.report.tasks_assigned.count}</p>
                                                            <p className="dsl-m12 mb-0 text-400">100%</p>
                                                        </div>
                                                        <div className="d-flex-1 text-end ssm-left d-flex d-sm-block py-2 py-sm-0 ai-center">
                                                            <span className="dsl-b14 mobile-screen d-flex-1">Incomplete For Month</span>
                                                            <p className="dsl-b14 lh-17 mb-1 ssm-b-0 text-400 me-0 ssm-r-2">{item.report.tasks_incomplete.count}</p>
                                                            <p className="dsl-m12 mb-0 text-400">{item.report.tasks_incomplete.percent}%</p>
                                                        </div>
                                                        <div className="d-flex-1 text-end ssm-left d-flex d-sm-block py-2 py-sm-0 ai-center">
                                                            <span className="dsl-b14 mobile-screen d-flex-1">Compl. For Month</span>
                                                            <p className="dsl-b14 lh-17 mb-1 ssm-b-0 text-400 me-0 ssm-r-2">{item.report.tasks_completed.count}</p>
                                                            <p className="dsl-m12 mb-0 text-400">{item.report.tasks_completed.percent}%</p>
                                                        </div>
                                                        <div className="d-flex-1 text-end ssm-left d-flex d-sm-block py-2 py-sm-0 ai-center">
                                                            <span className="dsl-b14 mobile-screen d-flex-1">Compl. on time</span>
                                                            <p className="dsl-b14 lh-17 mb-1 ssm-b-0 text-400 me-0 ssm-r-2">{item.report.tasks_completed_on_time.count}</p>
                                                            <p className="dsl-m12 mb-0 text-400">{item.report.tasks_completed_on_time.percent}%</p>
                                                        </div>
                                                        <div className="d-flex-1 text-end ssm-left d-flex d-sm-block py-2 py-sm-0 ai-center">
                                                            <span className="dsl-b14 mobile-screen d-flex-1">Compl. late</span>
                                                            <p className="dsl-b14 lh-17 mb-1 ssm-b-0 text-400 me-0 ssm-r-2">{item.report.tasks_completed_late.count}</p>
                                                            <p className="dsl-m12 mb-0 text-400">{item.report.tasks_completed_late.percent}%</p>
                                                        </div>
                                                        <div className="d-flex-1 text-end ssm-left d-flex d-sm-block py-2 py-sm-0 ai-center">
                                                            <span className="dsl-b14 mobile-screen d-flex-1">Ave Compl. Per Day</span>
                                                            <p className="dsl-b14 lh-17 mb-1 ssm-b-0 text-400">{item.report.avg_completions_per_day.toFixed(2)}</p>
                                                        </div>
                                                        <div className="d-flex-1 text-end ssm-left d-flex d-sm-block py-2 py-sm-0 ai-center">
                                                            <span className="dsl-b14 mobile-screen d-flex-1">Ave time Compl.</span>
                                                            <p className="dsl-b14 lh-17 mb-1 ssm-b-0 text-400">{convertAvgDays(item.report.avg_days.count)}</p>
                                                        </div>
                                                    </div>
                                                    <div className="pl-5 d-flex-3 d-flex flex-column flex-sm-row ssp-l-0">
                                                        <div className="d-flex-1 d-flex d-sm-block text-end pt-0 ssp-t-2">
                                                            <span className="dsl-b14 mobile-screen d-flex-1 text-start">Daily</span>
                                                            <p className="dsl-b14 lh-17 mb-1 text-400">{item.report.daily_completion.complete}</p>
                                                            <p className="dsl-b14 lh-17 mb-1 ms-0 ssm-l-2 text-400">{item.report.daily_completion.percent}%</p>
                                                        </div>
                                                        <div className="d-flex-1 d-flex d-sm-block text-end pt-0 ssp-t-2">
                                                            <span className="dsl-b14 mobile-screen d-flex-1 text-start">Weekly</span>
                                                            <p className="dsl-b14 lh-17 mb-1 text-400">{item.report.weekly_completion.complete}</p>
                                                            <p className="dsl-m12 mb-1 text-400 ms-0 ssm-l-2">{item.report.weekly_completion.percent}%</p>
                                                        </div>
                                                        <div className="d-flex-1 d-flex d-sm-block text-end pt-0 ssp-t-2">
                                                            <span className="dsl-b14 mobile-screen d-flex-1 text-start">Monthly</span>
                                                            <p className="dsl-b14 lh-17 mb-1 text-400">{item.report.monthly_completion.complete}</p>
                                                            <p className="dsl-m12 mb-1 text-400 ms-0 ssm-l-2">{item.report.monthly_completion.percent}%</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ))
                            ) : (
                                <p className="dsl-m12 text-center my-5">No records available</p>
                            )}
                        </div>
                    </div>
                </div>
                <Pagination
                    className="p-4"
                    current={current}
                    per={per}
                    total={Math.ceil(individuals.length / per)}
                    onPer={(e) => this.setState({ per: e })}
                    onChange={(e) => this.setState({ current: e })}
                />
            </div>
        );
    }
}

TaskReport.propTypes = {
    data: PropTypes.any,
    selected: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onClick: PropTypes.func,
    onTasks: PropTypes.func,
};

TaskReport.defaultProps = {
    data: [],
    selected: null,
    onClick: () => {},
    onTasks: () => {},
};

export default TaskReport;
